// The Dream Week forecast. api/internal/plan/forecast.go implements the same
// function for the Coach and Today, and api/internal/plan/testdata/
// forecast_cases.json holds both to the same answers: keep every float
// operation here in the same order as the Go.

export const DAYS_PER_MONTH = 30.4
export const HALF_DAY_MINUTES = 240
const CEIL_EPS = 1e-9

// Lever caps. Advice past these is not advice: "raise your price by 473%"
// reads as a verdict, not a way forward, so a lever past its cap is simply
// not offered.
const MAX_PRICE_RISE_PERCENT = 50
const MAX_ADD_HALF_DAYS = 7
const MIN_CLIENTS_CAP = 5

// Weekday 0 is Monday, as in working hours.
export interface Stretch {
  weekday: number
  start_minute: number
  end_minute: number
}

export interface ServiceMix {
  service_id: string
  service_length_id: string // shares are per length (spec §5)
  name: string // "Signature · 90 min" when the service has several lengths
  share: number
  duration_minutes: number
  buffer_after_minutes: number
  price_pence: number
}

export interface ForecastInputs {
  mix: ServiceMix[]
  demand_known: boolean
  monthly_demand: number
  practice_median_days: number
  return_rate: number
  active_clients: number
}

export interface GoalNumbers {
  take_home_target_pence: number
  monthly_costs_pence: number
  holiday_weeks_per_year: number
}

export type Limit = 'demand' | 'capacity' | 'no_hours'

export interface Levers {
  price_rise_percent: number | null
  add_half_days: number | null
  more_clients: number | null
  hours_wont_help: boolean
}

export interface Forecast {
  working_days: number
  hours_per_week: number
  weekly_capacity: number
  weeks_per_month: number
  monthly_capacity: number
  half_day_monthly_capacity: number
  monthly_demand: number
  demand_known: boolean
  visits: number
  limit: Limit
  avg_value_pence: number
  takings_pence: number
  take_home_pence: number
  occupancy: number
  gap_pence: number
  levers: Levers
}

// fits counts treatments of one service that fit a stretch. Each must end
// inside it; its blocked time may run past the end.
export function fits(stretchMinutes: number, duration: number, buffer: number): number {
  if (duration <= 0 || stretchMinutes < duration) return 0
  return Math.floor((stretchMinutes - duration) / (duration + buffer)) + 1
}

const ceil = (x: number) => Math.ceil(x - CEIL_EPS)

export function computeForecast(week: Stretch[], inputs: ForecastInputs, goal: GoalNumbers): Forecast {
  const days = new Set<number>()
  let minutes = 0
  for (const s of week) {
    days.add(s.weekday)
    minutes += s.end_minute - s.start_minute
  }
  const workingDays = days.size

  let weekly = 0
  let halfDay = 0
  let avg = 0
  for (const m of inputs.mix) {
    let n = 0
    for (const s of week) n += fits(s.end_minute - s.start_minute, m.duration_minutes, m.buffer_after_minutes)
    weekly += m.share * n
    halfDay += m.share * fits(HALF_DAY_MINUTES, m.duration_minutes, m.buffer_after_minutes)
    avg += m.share * m.price_pence
  }
  const avgValue = Math.round(avg)

  const weeksPerMonth = workingDays > 0 ? Math.max(0, 52 - goal.holiday_weeks_per_year) / 12 : 0
  const monthlyCapacity = weekly * weeksPerMonth
  const halfDayMonthly = halfDay * weeksPerMonth

  let limit: Limit
  let visits = 0
  if (monthlyCapacity <= 0) {
    limit = 'no_hours'
  } else if (inputs.demand_known && inputs.monthly_demand < monthlyCapacity) {
    limit = 'demand'
    visits = inputs.monthly_demand
  } else {
    limit = 'capacity'
    visits = monthlyCapacity
  }

  const takings = Math.round(visits * avgValue)
  const target = goal.take_home_target_pence + goal.monthly_costs_pence
  const gap = target - takings

  const levers: Levers = { price_rise_percent: null, add_half_days: null, more_clients: null, hours_wont_help: false }
  if (gap > 0 && limit !== 'no_hours' && avgValue > 0) {
    if (visits > 0) {
      const rise = ceil((target / visits / avgValue - 1) * 100)
      if (rise <= MAX_PRICE_RISE_PERCENT) levers.price_rise_percent = rise
    }
    const need = target / avgValue
    if (inputs.demand_known && need > inputs.monthly_demand && inputs.practice_median_days > 0) {
      const perClient = DAYS_PER_MONTH / inputs.practice_median_days
      const more = ceil((need - inputs.monthly_demand) / perClient)
      if (more <= Math.max(MIN_CLIENTS_CAP, inputs.active_clients)) levers.more_clients = more
    }
    const needsHours = need > monthlyCapacity && halfDayMonthly > 0
    if (needsHours) {
      const half = ceil((need - monthlyCapacity) / halfDayMonthly)
      if (half <= MAX_ADD_HALF_DAYS) levers.add_half_days = half
    }
    levers.hours_wont_help = limit === 'demand' && !needsHours
  }

  return {
    working_days: workingDays,
    hours_per_week: minutes / 60,
    weekly_capacity: weekly,
    weeks_per_month: weeksPerMonth,
    monthly_capacity: monthlyCapacity,
    half_day_monthly_capacity: halfDayMonthly,
    monthly_demand: inputs.demand_known ? inputs.monthly_demand : 0,
    demand_known: inputs.demand_known,
    visits,
    limit,
    avg_value_pence: avgValue,
    takings_pence: takings,
    take_home_pence: takings - goal.monthly_costs_pence,
    occupancy: monthlyCapacity > 0 ? visits / monthlyCapacity : 0,
    gap_pence: gap,
    levers,
  }
}
